import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useNotification } from '../NotificationSystem';
import { buildApiUrl } from '../../config/api';
import '../../styles/MeusCursos.css';

const MeusCursos = () => {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();

  useEffect(() => {
    fetchMeusCursos();
  }, []);

  const fetchMeusCursos = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(buildApiUrl('meus-cursos'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = response.data || [];

      setCursos(data);
    } catch (error) {

      showError('Erro ao carregar seus cursos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const hasPassedResult = (curso) => {
    const certificateAvailable = ['paid', 'available_for_purchase'].includes(curso.certificate_status);
    const passedExam = curso.user_results?.some(result => (
      result.passed === true || Number(result.passed) === 1
    ));

    return certificateAvailable || Boolean(passedExam);
  };

  const getStatusMessage = (curso) => {
    if (hasPassedResult(curso)) {
      return 'Concluído';
    }

    const result = curso.user_results?.[0];
    if (result) {
      return 'Não Aprovado';
    }
    return 'Em Andamento';
  };

  const getStatusClass = (curso) => {
    if (hasPassedResult(curso)) {
      return 'completed';
    }

    const result = curso.user_results?.[0];
    if (result) {
      return 'failed';
    }
    return 'studying';
  };

  const getButton = (curso) => {
    if (hasPassedResult(curso)) {
      switch (curso.certificate_status) {
        case 'paid':
          return (
            <Link 
              to="/meus-certificados" 
              className="curso-certificado-liberado-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Imprimir Certificado
            </Link>
          );
        case 'available_for_purchase':
          return (
            <Link 
              to={`/cart/add?curso_id=${curso.id}`} 
              className="curso-certificado-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 11L9 14L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Emitir Certificado
            </Link>
          );
        default:
          return (
            <Link 
              to={`/cart/add?curso_id=${curso.id}`} 
              className="curso-certificado-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 11L9 14L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Emitir Certificado
            </Link>
          );
      }
    }
    
    return (
      <Link 
        to={`/curso/${curso.id}/estudo`} 
        className="curso-button"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polygon points="5,3 19,12 5,21" fill="currentColor"/>
        </svg>
        Continuar
      </Link>
    );
  };

  const formatHours = (hours) => {
    return hours ? `Carga horária: ${hours} horas` : 'Carga horária não informada';
  };

  const calculateStats = () => {
    const total = cursos.length;
    const completed = cursos.filter(hasPassedResult).length;
    const certificates = cursos.filter(curso => curso.certificate_status === 'paid').length;
    const totalHours = cursos.reduce((acc, curso) => acc + (parseInt(curso.hours) || 0), 0);

    return { total, completed, certificates, totalHours };
  };

  if (loading) {
    return (
      <div className="meus-cursos-page">
        <div className="meus-cursos-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!Array.isArray(cursos)) {
    return (
      <div className="meus-cursos-page">
        <div className="meus-cursos-container">
          <div className="loading-message">
            <p>Erro ao carregar cursos.</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = calculateStats();

  const cursosAprovados = cursos.filter(hasPassedResult);
  const cursosEmAndamento = cursos.filter(curso => !hasPassedResult(curso));

  return (
    <div className="meus-cursos-page">
      {/* Hero Header */}
      <div className="meus-cursos-hero">
        <div className="hero-content">
          <h1>Meus Cursos</h1>
          <p className="hero-subtitle">Acompanhe seu progresso e continue aprendendo com nossos cursos gratuitos</p>
        </div>
      </div>

      <div className="meus-cursos-container">{cursos.length === 0 ? (
          /* Estado Vazio */
          <div className="empty-state">
            <div className="empty-icon">
              📚
            </div>
            <h2 className="empty-title">Nenhum curso encontrado</h2>
            <p className="empty-message">
              Você ainda não se matriculou em nenhum curso. Explore nossa biblioteca 
              com centenas de cursos gratuitos e comece sua jornada de aprendizado hoje mesmo!
            </p>
            <Link to="/cursos" className="empty-cta">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Explorar Cursos
            </Link>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon courses">📚</div>
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Cursos matriculados</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon completed">✅</div>
                <div className="stat-number">{stats.completed}</div>
                <div className="stat-label">Cursos concluídos</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon certificates">🏆</div>
                <div className="stat-number">{stats.certificates}</div>
                <div className="stat-label">Certificados emitidos</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon hours">⏱️</div>
                <div className="stat-number">{stats.totalHours}h</div>
                <div className="stat-label">Carga horária total</div>
              </div>
            </div>

            {/* Lista de Cursos */}
            <div className="cursos-list-container">
              {/* Seção: Concluídos */}
              {cursosAprovados.length > 0 && (
                <>
                  <div className="cursos-section-header cursos-section-concluidos">
                    <span className="section-icon" aria-hidden="true">🏆</span>
                    <div className="section-copy">
                      <h2>Cursos concluídos — emita seu certificado!</h2>
                      <p>Você concluiu e foi aprovado nos cursos abaixo. Registre suas conquistas profissionais.</p>
                    </div>
                    <span className="section-count" aria-label={`${cursosAprovados.length} cursos concluídos`}>{cursosAprovados.length}</span>
                  </div>
                  <div className="cursos-list cursos-list--cards">
                    {cursosAprovados.map((curso) => (
                      <article key={curso.id} className="curso-item curso-item--aprovado">
                        <div className="curso-main">
                          <span className="curso-icon curso-icon--completed" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11a3 3 0 0 1 3 3v14a3 3 0 0 0-3-3H6.5A2.5 2.5 0 0 0 4 19.5v-14Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                              <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H14v17a3 3 0 0 1 3-3h.5a2.5 2.5 0 0 1 2.5 2.5v-14Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                            </svg>
                          </span>
                          <div className="curso-info">
                            <h3 className="curso-title">{curso.title}</h3>
                            <span className="hours-badge">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="12,7 12,12 15.5,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {formatHours(curso.hours)}
                            </span>
                          </div>
                        </div>
                        <div className="curso-card-footer">
                          <span className="status-badge completed">✓ Aprovado</span>
                          <div className="curso-actions">{getButton(curso)}</div>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              )}

              {/* Seção: Em Andamento */}
              {cursosEmAndamento.length > 0 && (
                <>
                  <div className="cursos-section-header cursos-section-andamento">
                    <span className="section-icon" aria-hidden="true">📖</span>
                    <div className="section-copy">
                      <h2>Cursos em andamento</h2>
                      <p>Continue de onde parou e avance no seu ritmo.</p>
                    </div>
                    <span className="section-count" aria-label={`${cursosEmAndamento.length} cursos em andamento`}>{cursosEmAndamento.length}</span>
                  </div>
                  <div className="cursos-list cursos-list--cards">
                    {cursosEmAndamento.map((curso) => (
                      <article key={curso.id} className="curso-item">
                        <div className="curso-main">
                          <span className="curso-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11a3 3 0 0 1 3 3v14a3 3 0 0 0-3-3H6.5A2.5 2.5 0 0 0 4 19.5v-14Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                              <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H14v17a3 3 0 0 1 3-3h.5a2.5 2.5 0 0 1 2.5 2.5v-14Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                            </svg>
                          </span>
                          <div className="curso-info">
                            <h3 className="curso-title">{curso.title}</h3>
                            <span className="hours-badge">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="12,7 12,12 15.5,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {formatHours(curso.hours)}
                            </span>
                          </div>
                        </div>
                        <div className="curso-card-footer">
                          <span className={`status-badge ${getStatusClass(curso)}`}>
                            {getStatusMessage(curso)}
                          </span>
                          <div className="curso-actions">{getButton(curso)}</div>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MeusCursos;
